import {useTranslation} from 'react-i18next';
import Anchor from '../../../common/component/Anchor';
import Row from '../../../common/component/Row';
import SingleText from '../../../common/component/SingleText';
import Text from '../../../common/component/Text';
import TextButton from '../../../common/component/TextButton';
import {Page} from '../../../common/constant/page';
import {ITicketType} from '../../../common/type';
import {routeTo} from '../../../common/util/PageRouter';
import {sendEvent} from '../../../common/util/tracking';
import {TRACKING_PAGE_NAME} from '../../common';
import {IContentAddedTicketItem, IContentTicketType} from '../../type';

interface IProps {
  addedItems: IContentAddedTicketItem;
  contentTicketType: IContentTicketType;
  id: number;
  sid: string;
}

export default function ContentTicketAddedItemV4({
  addedItems,
  contentTicketType,
  id,
  sid,
}: IProps) {
  const {t} = useTranslation();
  const {
    mediumTextClassName,
    regularTextClassName,
    cancelTextClassName,
    cancelClassName,
  } = CONTENT_TICKET_ADDED_ITEM_CN_MAP[contentTicketType];
  const {
    purchaseId,
    type,
    ticketCount,
    hasRemainTicket,
    isEventGift,
    isNewbie,
    isExpired,
    isUsed,
    isRefunded,
    addedDate,
    remainExpireText,
  } = addedItems;
  const showRefund =
    (type === 'rental' || type === 'possession') &&
    !isRefunded &&
    !isExpired &&
    hasRemainTicket;
  const ticketStatusText =
    (isRefunded
      ? t('contenthome_tickethistory_cancelled')
      : isUsed
        ? t('common_use_past')
        : isExpired
          ? t('common_expired')
          : remainExpireText) || undefined;

  return (
    <>
      <Row
        className="overflow-visible"
        rightWrapperClassName={cn('relative', {
          'w-1/2 flex-none self-start': showRefund,
        })}
        rightElement={
          showRefund &&
          purchaseId && (
            <Anchor
              pageInfo={{
                page: Page.ContentTicketRefund,
                id,
                sid,
                ticketId: purchaseId,
              }}
              className="absolute !items-end right-0 top-6"
              onClick={() => {
                sendEvent({
                  section: 'content_home',
                  page:
                    contentTicketType === 'contentTicketTab'
                      ? TRACKING_PAGE_NAME.ticket
                      : 'content_ticketpurchase_history',
                  actionName: 'ticket_cancel',
                  action: 'click',
                  customProps: {
                    page_content_id: id,
                  },
                });
                routeTo({
                  page: Page.ContentTicketRefund,
                  id,
                  sid,
                  ticketId: purchaseId,
                });
              }}
            >
              <TextButton
                textClassName={cn('s12-medium-white', cancelTextClassName)}
                className={cn('btn-transparent-grey-05 px-12', cancelClassName)}
                height="28px"
              >
                {t('ticket_purchase_cancel')}
              </TextButton>
            </Anchor>
          )
        }
      >
        <SingleText className={mediumTextClassName}>
          {t(CONTENT_ADDED_TICKET_TEXT_KEY_MAP[type], {
            count: ticketCount,
          })}
        </SingleText>
      </Row>
      {(isEventGift || isNewbie) && (
        <Text className={cn(mediumTextClassName, 'mt-2')}>
          {t(
            isEventGift
              ? 'contenthome_tickethistory_gift_event'
              : 'ticket_history_used_newbie'
          )}
        </Text>
      )}
      <Row
        className="mt-7"
        rightElement={
          ticketStatusText && (
            <Text className={regularTextClassName}>{ticketStatusText}</Text>
          )
        }
      >
        <Text className={regularTextClassName}>{addedDate}</Text>
      </Row>
    </>
  );
}

const CONTENT_ADDED_TICKET_TEXT_KEY_MAP: {[key in ITicketType]: string} = {
  rental: 'common_ticket_rental_amount',
  possession: 'common_ticket_possession_amount',
  gift: 'common_ticket_gift_amount',
  welcomeGift: 'common_ticket_gift_amount',
  waitForFree: 'common_ticket_gidamu_amount',
};

const CONTENT_TICKET_ADDED_ITEM_CN_MAP: {
  [key in IContentTicketType]: {
    mediumTextClassName: string;
    regularTextClassName: string;
    cancelTextClassName?: string;
    cancelClassName?: string;
  };
} = {
  contentTicketTab: {
    mediumTextClassName: 's14-medium-white opacity-85',
    regularTextClassName: 's13-regular-white opacity-50',
    cancelClassName: 'border-white/15',
  },
  contentTicketHistory: {
    mediumTextClassName: 's14-medium-white light:text-black',
    regularTextClassName: 's13-regular-grey06',
    cancelTextClassName: 'light:text-black',
  },
};
